import React, {useEffect, useMemo, useState } from "react";
import ProductModel from "@/models/server/ProductModel";
import { getProductWithDiscounts } from "@/services/ProductService";
import styles from "@/components/Product/ProductPrice/ProductPrice.module.scss";
import { LoadingDecorator } from "@renta-apps/renta-react-components";
import Prices from "@/components/Prices/Prices";
import PriceHelper from "@/helpers/PriceHelper";
import GetProductWithDiscountsRequest from "@/models/server/Requests/GetProductWithDiscountsRequest";
import ProductVatButtons from "@/components/ProductVatButtons/ProductVatButtons";

interface IProductPriceProps {
    product: ProductModel;
    contractId: string | null;
    constructionSiteId: string | null;
    showCustomerPrices: boolean;
    isAuthenticated: boolean;
    isAsyncCustomerPrice: boolean;  // flag determines whether discounts should be loaded asynchronously
}

const ProductPrice: React.FC<IProductPriceProps> = ({product, contractId, constructionSiteId, showCustomerPrices, isAuthenticated, isAsyncCustomerPrice}) => {
    const [loading, setLoading] = useState(true);
    const [customerPrices, setCustomerPrices] = useState(showCustomerPrices);
    const [productWithDiscounts, setProductWithDiscounts] = useState(product);

    const vat = useMemo(() => {
        return customerPrices
            ? 0
            : PriceHelper.environmentVat;
    }, [customerPrices]);

    const hasDiscountedPrices = useMemo(() => {
        if (productWithDiscounts === null) {
            return false;
        }

        return ((productWithDiscounts.customerDailyPrice !== undefined) && (productWithDiscounts.customerDailyPrice !== null)) ||
            ((productWithDiscounts.customerMonthlyPrice !== undefined) && (productWithDiscounts.customerMonthlyPrice !== null));
    }, [productWithDiscounts]);

    useEffect(() => {
        try {
            const fetchData = async () => {
                const request: GetProductWithDiscountsRequest = {
                    product,
                    contractId,
                    constructionSiteId,
                };

                const updatedProduct = await getProductWithDiscounts(request);
                setProductWithDiscounts(updatedProduct);
                setLoading(false);
            };

            if (isAuthenticated && isAsyncCustomerPrice && loading) {
                fetchData();
            }
        } catch (error) {
            console.error('Error loading product price:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    return (
        <LoadingDecorator className={styles.loadingProductPriceContainer} hideContentOnLoading={true} isLoading={loading}>
            <Prices displayWeeklyPriceTooltip displayDiscountText inlinePriceRows
                    className={styles.pricesContainer}
                    product={productWithDiscounts}
                    vat={vat}
            />
            {(!hasDiscountedPrices) && (
                <ProductVatButtons
                    environmentVatValue={PriceHelper.environmentVat}
                    isZeroVatSelected={customerPrices}
                    isEnvVatSelected={!customerPrices}
                    onVatChangeAsync={async (value) => setCustomerPrices(value === 0)}
                />
            )}
        </LoadingDecorator>
    );
};

export default ProductPrice;